import FetchFactory from "~/repository/factory";

class V2Module extends FetchFactory {

  async getEvents() {
    // Direct ofetch (not useAsyncData): useAsyncData rewraps the offline FetchError into a
    // generic 500 NuxtError, which createDataLoadError can no longer tell apart from a real
    // server error — so the home page rendered "Something broke" instead of "You're offline".
    const response = await this.clientGet<{data: EventBasic[]}>('/v2/events');
    return response?.data ?? [];
  }

  async getEvent(id: number) {
    const response = await this.clientGetNoAuth<{data: EventData}>(`/v2/events/${id}`);
    return response ? response.data : null;
  }

  async getEventPartnersByEventId(id: number) {
    // Public endpoint (like getEvent) — no auth header, so Cloudflare can cache it.
    const response = await this.clientGetNoAuth<{data: EventPartners}>(`/v2/events/${id}/partners`);
    return response ? response.data : null;
  }

  async getEventPartnerRelatedJobs(id: number) {
    const response = await this.get<JobBasic>(`/v2/events/${id}/partners`, `event-${id}-partners`, {
      transform: (r: EventPartners|any) => {return r.data}
    });
    return response ?? null;
  }

  async getAds(adsCount:number = 3) {
    const response = await this.get(`/v2/ads/community-partners?ads_count=${adsCount}`, 'ads', {
      transform: (r: any) => {return r.data}
    });
    return  response ?? [];
  }

  async getJobRecommendationsBySkills(skills:Array<number> = []) {
    const response = await this.get(`/v2/jobs/recommendations?skills=${skills}`, 'jobs-recommendations', {
      transform: (r: any) => {return r.data}
    });
    return  response ?? [];
  }

  async getVimeoChatUrl(eventId: number) {
    const response = await this.get<{ vimeo_chat_url: string|null }>(`/v2/events/${eventId}/vimeo-chat-url`, `event-${eventId}-vimeo-chat-url`);
    return response ?? { vimeo_chat_url: null };
  }

  async getContactDetails(eventId: number){
    const response = await this.clientGet<EventUserContactDetails>(`/v2/events/${eventId}/contact-details`);
    return response ? response.data as EventUserContactDetails : {};
  }

  async updateContactDetails(eventId: number, formData: any): Promise<EventUserContactDetails> {
    const response = await this.clientPost(`/v2/events/${eventId}/contact-details`, formData);
    return (response ? response.data : {}) as EventUserContactDetails;
  }

  async acknowledgeLeadScanningNotice(eventId: number) {
    return await this.clientPost(`/v2/events/${eventId}/contact-details/lead-scanning-notice`, {});
  }

  //region Tracking
  async trackEvent(payload: TrackingPayload) {
    return await this.clientPost('/v2/tracking/event', payload);
  }

  // Batched click tracking (TAL-11043), see composables/useTracking.ts. keepalive: the batch sent as the page is
  // hidden has to outlive the page.
  async postAppTracking(eventId: number, body: object) {
    return await this.clientPost(`/v2/events/${eventId}/tracking`, body, {keepalive: true});
  }
  //endregion

  //region App Tickets and access token validation

  async checkToken(token: string) {
    return await this.post<EventTicketValidation>(`/v2/user/ticket/access-token`, 'check-access-token', {
      'ticket_token': token,
    });
  }

  async linkTicket(payload: LinkTicketRequest) {
    const response = await this.clientPost<EventBasic[]>(`/v2/user/ticket/link-ticket`, payload);
    return response ?? [];
  }

  //endregion

  //region User Sessions

  async getWorkshopsCounts(eventId: number) {
    const res = await this.call('GET',`/v2/events/${eventId}/workshops/counts`);
    return (res as any).data ?? res ?? [];
  }

  async setBookmarkStatus(eventId: number, type: EventBookmarkType, objectId: number, status: boolean) {
    const response = await this.clientPost<number[]|BookmarkedSession[]>(
        `/v2/events/${eventId}/bookmark/${type}/${objectId}/${status ? 'on' : 'off'}`
    );
    return response ?? [];
  }

  // The plan an AI agent suggested, after the user confirmed it (wad-api: POST /v2/events/{id}/bookmarks/sessions).
  // Workshops, cancelled and unknown sessions are skipped by the API and reported with a reason.
  async bulkBookmarkSessions(eventId: number, sessionIds: number[]): Promise<BulkBookmarkResult> {
    const response = await this.clientPost<{data: BulkBookmarkResult}>(
        `/v2/events/${eventId}/bookmarks/sessions`,
        { session_ids: sessionIds }
    );
    return response?.data ?? {added: [], already_bookmarked: [], skipped: []};
  }

  async getBookmarks(eventId: number): Promise<{ sessions: BookmarkedSession[]; speakers: number[], partners: number[], goodies: number[], activities: number[] }> {
    const response = await this.clientGet<{data: { sessions: BookmarkedSession[]; speakers: number[]; partners: number[]; goodies: number[]; activities: number[]}}>(
        `/v2/events/${eventId}/bookmarks`
    );
    return response ? response.data : {sessions: [],speakers: [], partners: [], goodies: [], activities: []};
  }

  async createSessionQuestion(eventId: number, sessionId: number, question: string) {
    const response = await this.clientPost<{ success: boolean }>(
        `/v2/events/${eventId}/sessions/${sessionId}/questions`,
        { question }
    );
    return response?.success ?? false;
  }

  async getSessionQuestions(eventId: number, sessionId: number) {
    // Direct ofetch (not useAsyncData): the questions page awaits this at the top of setup and
    // routes failures through createDataLoadError. useAsyncData rewraps the offline FetchError
    // into a 500 NuxtError, so the page showed "Something broke" instead of "You're offline".
    const response = await this.clientGet<{data: SessionQuestion[]}>(`/v2/events/${eventId}/sessions/${sessionId}/questions`);
    return response?.data ?? [];
  }

  async updateQuestionAnswerStatus(questionId: number, checkedQuestion: boolean, eventId: number, sessionId: number) {
    return await this.clientPost(`/v2/events/${eventId}/sessions/${sessionId}/questions/${questionId}/answer-status`, {
      checked_question: checkedQuestion,
    });
  }

  //endregion

  //region User Notification

  async storeUserFirebaseCloudMessagingToken(payload: object) {
    return await this.clientPost('/v2/notifications/fcm', payload);
  }

  async removeUserFirebaseCloudMessagingToken(payload: object) {
    return await this.clientPost('/v2/notifications/remove-fcm', payload);
  }

  async unsubscribeFromFirebaseNotifications(payload: object) {
    return await this.clientPost('/v2/notifications/unsubscribe', payload);
  }

  // Returns the whole envelope, not just `data`: the badge count lives in `meta` (NotificationsStore).
  async getUserNotifications(markAsRead: boolean = false): Promise<AppNotificationsResponse> {
    return await this.clientGet<AppNotificationsResponse>(`/v2/notifications/user/app${markAsRead ? '?mark-as-read=true' : ''}`);
  }

  //endregion

  //region User related

  async storeUserNotificationStatus(payload: object) {
    return await this.clientPost('/v2/user/notifications', payload);
  }

  async verifyUserEmail(token: string) {
    return await this.post('/v2/user/email/verify', 'user-email-verification', {
      'token': token,
      // Same reason as the login call: the retirement gate is the portal's, not the app's.
      'origin': 'app'
    });
  }

  async resendUserEmailVerificationCode() {
    return await this.post('/v2/user/email/resend-app-verification', 'user-email-verification-resend', {});
  }

  async forgotPassword(email: string) {
    // The origin makes the API build the email link back to this app instead
    // of the main site.
    return await this.clientPostNoAuth('/v2/user/password/forgot', { email, origin: 'app' });
  }

  async resetPassword(payload: { email: string, token: string, password: string, password_confirmation: string }) {
    return await this.clientPostNoAuth('/v2/user/password/reset', payload);
  }

  //endregion

  //region User settings
  //
  // Backs pages/settings.vue. Scope is deliberately narrow: only the account data a
  // user can actually originate inside this app, which is their email and password (set
  // at signup) plus the account itself. Profile, location, languages and interests are
  // entered on the web platform and stay there — an app-side copy of those would sit one
  // tap away from the per-event contact details on the badge page and be mistaken for it.
  //
  // All client* calls — the page is behind auth and never server-rendered for a
  // signed-out visitor, so there is nothing to gain from useAsyncData caching here.

  async updateUserName(payload: UpdateNamePayload) {
    return await this.clientPut('/v2/user/name', payload);
  }

  async updateUserPassword(payload: UpdatePasswordPayload) {
    return await this.clientPut('/v2/user/password', payload);
  }

  // Avatar change is a three-step exchange: this returns a presigned S3 PUT URL, the
  // file bytes go straight to that URL from the browser (NOT through this factory — it
  // would attach an Authorization header and JSON content type, both of which break the
  // signed request), then the returned `key` is registered via updateUserAccountImage.
  async getUserImageUploadUrl(payload: ImageUploadUrlPayload) {
    return await this.clientPost<ImageUploadUrlResponse>('/v2/user/image/upload-url', payload);
  }

  async updateUserAccountImage(payload: UpdateAccountImagePayload) {
    return await this.clientPut('/v2/user/image', payload);
  }

  // For accounts created through OAuth that have no password yet: the API mails a token
  // first (sendPasswordSetupVerificationCode), which is then exchanged here.
  async setUserPassword(payload: SetPasswordPayload) {
    return await this.clientPost('/v2/user/password/set', payload);
  }

  async sendPasswordSetupVerificationCode() {
    return await this.clientPost('/v2/user/password/setup-verification', {});
  }

  // Three-step exchange: step 1 mails a security code to the CURRENT address, step 2
  // submits that code together with the new address, step 3 confirms the code sent to
  // the new address. Each step validates a different set of fields server-side.
  async changeUserEmail(payload: ChangeEmailPayload) {
    return await this.clientPost('/v2/user/email/change', payload);
  }

  // Two endpoints, one per account type, and they are not interchangeable:
  // delete-generic-account returns 404 "Generic account not found" for a developer
  // account, so calling the wrong one looks to the user like a broken button rather
  // than a refusal. Both validate `current_password`.
  async deleteGenericAccount(payload: DeleteAccountPayload) {
    return await this.clientPost('/v2/user/delete-generic-account', payload);
  }

  async deleteDeveloperAccount(payload: DeleteAccountPayload) {
    return await this.clientPost('/v2/developer/delete-account', payload);
  }

  //endregion

  //region Contacts/Leads

  async getEventLeadsPlain (
      eventId: number,
      order: OrderOpt = 'newest',
      page: number,
      search: string = '',
  ): Promise<Paginated<EventLead>> {
    const params: Record<string, string> = {
      sort: order,
      page: page.toString(),
    };
    if(search) {
      params.search = search;
    }
    const query = new URLSearchParams(params).toString();

    const url  = `/v2/events/${eventId}/contacts?${query}`;

    return await this.call<Paginated<EventLead>>('GET', url);
  }

  async getEventLead (
      eventId: number,
      contactId: number
  ): Promise<EventLead> {
    const url  = `/v2/events/${eventId}/contacts/${contactId}`;

    const res  = await this.call<EventLead>('GET', url);

    return (res as any).data ?? res ?? [];
  }

  async updateContact(eventId: number, contactId: number, formData: any) {
    // clientPost: mutations must not go through useAsyncData — a repeated key replays the cached response instead of saving
    const response = await this.clientPost<{data: EventLead}>(`/v2/events/${eventId}/contacts/${contactId}`, formData);
    return response?.data ?? {};
  }

  async scanEventLead(eventId: number, code: string): Promise<EventLead> {
    const response = await this.clientPost<{data: EventLead}>(`/v2/events/${eventId}/contacts/scan`, {code});
    return response?.data ?? {} as EventLead;
  }

  async clientScanEventLead(eventId: number, code: string): Promise<EventLead> {
    const response = await this.clientPost<{data: EventLead}>(`/v2/events/${eventId}/contacts/scan`, {code});
    return response?.data ?? {} as EventLead;
  }

  async sendEventLeadsToMail(eventId: number) {
    const response = await this.clientPost<{data: any}>(`/v2/events/${eventId}/contacts/send-to-mail`, {});
    return response?.data ?? {};
  }

  // Available lead tags for the user's company at this event (managed in the partner portal)
  async getEventLeadTags(eventId: number): Promise<LeadTag[]> {
    const res = await this.call<LeadTag[]>('GET', `/v2/events/${eventId}/lead-tags`);
    return Array.isArray(res) ? res : ((res as any)?.data ?? []);
  }

  //endregion

  //region Event homepage

  /**
   * A plain client fetch, not this.get(): the only caller is a watcher on the event id, which
   * fires once the store has loaded the event — after mount. useAsyncData in that position
   * warns "Component is already mounted, please use $fetch instead" and keys a cache entry
   * nothing reuses, because the page reads the returned value straight into its own ref.
   */
  async getEventHomepageData(eventId: number) {
    const response = await this.clientGet<{data: HomepageResponse}>(`/v2/events/${eventId}/homepage`);
    return response?.data ?? null;
  }

  //endregion

  //region Event Attendees

  /**
   * Get event attendees list
   *
   * @param eventId
   * @param page
   * @param filter
   */
  async getEventAttendees(eventId: number, page: number, filter: AttendeesFilter): Promise<Paginated<EventAttendee>> {
    return await this.call<Paginated<EventAttendee>>('GET', `/v2/events/${eventId}/attendees?page=${page}&filter=${filter}`);
  }

  /**
   * Request new attendee connection
   *
   * @param eventId
   * @param payload
   */
  async requestNewAttendeeConnection(eventId: number, payload: EventAttendeeNewConnectionRequest): Promise<EventAttendeeRequestNewConnectionResponse> {
    return await this.call<EventAttendeeRequestNewConnectionResponse>('POST', `/v2/events/${eventId}/attendees/new-connection`, payload);
  }

  /**
   * Request new attendee direct badge scan connection
   *
   * @param eventId
   * @param payload
   */
  async attendeeDirectBadgeScan(eventId: number, payload: EventAttendeeDirectBadgeScanRequest): Promise<EventAttendeeDirectBadgeScanResponse> {
    return await this.call<EventAttendeeDirectBadgeScanResponse>('POST', `/v2/events/${eventId}/attendees/badge-scan`, payload);
  }

  /**
   * Update attendee connection
   *
   * @param eventId
   * @param payload
   */
  async updateAttendeeConnection(eventId: number, payload: EventAttendeeNewConnectionRequest): Promise<EventAttendeeRequestNewConnectionResponse> {
    return await this.clientPost<EventAttendeeRequestNewConnectionResponse>( `/v2/events/${eventId}/attendees/connection`, payload);
  }

  async updateAttendeeConnectionNotes(eventId: number, payload: EventAttendeeNotesRequest): Promise<SuccessResponse> {
    return await this.call<SuccessResponse>('POST', `/v2/events/${eventId}/attendees/notes`, payload);
  }

  async searchEventAttendees(eventId: number, term: string, page: number, filter: AttendeesFilter): Promise<Paginated<EventAttendee>> {
    // `term` is whatever was typed into the search box. Interpolated raw, an & started a new
    // parameter, a # truncated the query and a stray % made it invalid — so searching for a
    // company like "M&S" queried something else entirely.
    const query = new URLSearchParams({
      term,
      page: String(page),
      filter,
    }).toString();

    return await this.call<Paginated<EventAttendee>>('GET', `/v2/events/${eventId}/attendees/search?${query}`);
  }

  async getEventAttendeeDetails(eventId: number, userId: number): Promise<EventAttendeeDetails|null> {
    const response = await this.clientGet<{data: EventAttendeeDetails}>(`/v2/events/${eventId}/attendees/${userId}/details`);
    return  response ? response.data : null;
  }

  //endregion

  //region Chat

  /**
   * Open (or create) the DM thread with an attendee, with its most recent page.
   *
   * Event-scoped so the API can apply its ticket check; every call after this
   * addresses the conversation by id.
   */
  async openEventChat(eventId: number, attendeeId: number): Promise<ChatConversation|null> {
    const response = await this.clientGet<{data: ChatConversation}>(`/v2/events/${eventId}/attendees/${attendeeId}/chat`);
    return response ? response.data : null;
  }

  /**
   * A page of messages.
   *
   * `after_id` is the reconnect backfill — everything missed while the SSE connection
   * was down, oldest first. `before_id` pages backwards for scroll-back. Mutually
   * exclusive; pass one.
   */
  async getChatMessages(conversationId: number, params: {before_id?: number, after_id?: number, limit?: number} = {}): Promise<ChatMessagePage> {
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    const url = `/v2/chat/conversations/${conversationId}/messages${query ? `?${query}` : ''}`;

    return await this.clientGet<ChatMessagePage>(url);
  }

  /**
   * Send a message.
   *
   * clientPost, not post: mutations must not go through useAsyncData — a repeated key
   * replays the cached response instead of sending.
   *
   * `client_message_id` is what makes a retry idempotent. The API answers 201 for a
   * message it stored and 200 for one it already had, so resending after a timeout
   * returns the original rather than duplicating it.
   */
  async sendChatMessage(conversationId: number, body: string, clientMessageId: string): Promise<ChatMessage|null> {
    const response = await this.clientPost<{data: ChatMessage}>(`/v2/chat/conversations/${conversationId}/messages`, {
      body,
      client_message_id: clientMessageId,
    });
    return response ? response.data : null;
  }

  /**
   * Delete your own message. The row survives as a tombstone — the API returns it
   * with `deleted: true` and no body, so the thread keeps its shape.
   *
   * No clientDelete on FetchFactory, so this goes through the generic call().
   */
  async deleteChatMessage(messageId: number): Promise<ChatMessage|null> {
    const response = await this.call<{data: ChatMessage}>('DELETE', `/v2/chat/messages/${messageId}`);
    return response ? response.data : null;
  }

  /**
   * Toggle one reaction. Returns the message's full reaction set, so the caller
   * replaces rather than reconciles.
   */
  async toggleChatReaction(messageId: number, emoji: string): Promise<ChatReactionResponse|null> {
    const response = await this.clientPut<{data: ChatReactionResponse}>(`/v2/chat/messages/${messageId}/reactions`, {emoji});
    return response ? response.data : null;
  }

  //endregion

  //region OAuth

  async initOAuth(provider: string, redirect_uri: string): Promise<any> {
    return await this.call<any>('GET', `/v2/oauth/${provider}/init?redirect_uri=${redirect_uri}`);
  }

  async exchangeOAuth(provider: string, payload: object): Promise<any> {
    return await this.call<any>('POST', `/v2/oauth/${provider}/exchange`, payload);
  }

  async sendOAuthCode(payload: object): Promise<any> {
    return await this.call<any>('POST', `/v2/oauth/send-code`, payload);
  }

  async confirmOAuthCode(payload: object): Promise<any> {
    return await this.call<any>('POST', `/v2/oauth/confirm-code`, payload);
  }

  async finalizeOAuthCode(payload: object): Promise<any> {
    return await this.call<any>('POST', `/v2/oauth/finalize`, payload);
  }


  //endregion

}

export default V2Module;
